import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "./components/Header.jsx";
import Footer from "./components/Footer.jsx";
import ReviewCard from "./components/ReviewCard.jsx";

const TAG_LABELS = {
  "atai-2026": "All Things AI 2026",
  "newsletter": "Newsletter readers",
  "tri-women-in-stem": "Triangle Women in STEM",
  "ai-for-gtm": "AI for GTM",
  "aios-desktop": "AIOS Desktop",
  "ai-forward": "AI Forward",
};

// Super-categories group multiple tags into one filter chip.
// Order matters — this is also the chip display order.
const CATEGORIES = [
  {
    key: "events",
    label: "Events",
    icon: "🎤",
    tags: ["atai-2026", "tri-women-in-stem", "ai-forward"],
  },
  {
    key: "newsletter",
    label: "Newsletter",
    icon: "✉️",
    tags: ["newsletter"],
  },
  {
    key: "training",
    label: "Training",
    icon: "🎓",
    tags: ["aios-desktop", "ai-for-gtm"],
  },
];

export default function App() {
  const [all, setAll] = useState([]);
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("featured");
  const [rating, setRating] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/data/testimonials.json")
      .then((r) => r.json())
      .then((d) => {
        setAll(d.filter((t) => t.status === "published"));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    if (!all.length) return { count: 0, avg: 0, fiveStar: 0 };
    const rated = all.filter((t) => typeof t.rating === "number" && t.rating > 0);
    const avg = rated.length
      ? rated.reduce((s, t) => s + t.rating, 0) / rated.length
      : 0;
    return {
      count: all.length,
      avg: avg.toFixed(2),
      fiveStar: rated.filter((t) => t.rating === 5).length,
    };
  }, [all]);

  const tagCounts = useMemo(() => {
    const m = {};
    all.forEach((r) => (r.tags || []).forEach((t) => (m[t] = (m[t] || 0) + 1)));
    return m;
  }, [all]);

  const featured = useMemo(() => {
    const SENIOR =
      /\b(ceo|cto|cfo|coo|chief|founder|co-?founder|president|vp|vice president|director|head of|head\b|partner|principal|managing|svp|evp|owner)\b/i;

    // Never featured: anyone with display.featured_exclude
    const eligible = all.filter((t) => !t.display?.featured_exclude);

    // Group A: explicit priority picks (Brad Wolverton, Ryan Heafy, etc.)
    const priority = eligible.filter((t) => t.display?.featured_priority);

    // Group B: senior-title 5-star with both avatar + logo, sorted by impact
    const senior = eligible
      .filter(
        (t) =>
          t.rating === 5 &&
          t.customer.avatar_url &&
          t.customer.title &&
          SENIOR.test(t.customer.title) &&
          !t.display?.featured_priority
      )
      .sort((a, b) => (b.word_count || 0) - (a.word_count || 0));

    // Dedup by id, cap at 6
    const seen = new Set();
    const out = [];
    for (const t of [...priority, ...senior]) {
      if (seen.has(t.id)) continue;
      seen.add(t.id);
      out.push(t);
      if (out.length >= 6) break;
    }
    return out;
  }, [all]);

  const categoryCounts = useMemo(() => {
    const m = {};
    for (const cat of CATEGORIES) {
      m[cat.key] = all.filter((r) =>
        (r.tags || []).some((t) => cat.tags.includes(t))
      ).length;
    }
    return m;
  }, [all]);

  const filtered = useMemo(() => {
    let out = all;
    if (rating) out = out.filter((r) => r.rating >= rating);
    if (category) {
      const cat = CATEGORIES.find((c) => c.key === category);
      if (cat) out = out.filter((r) => (r.tags || []).some((t) => cat.tags.includes(t)));
    }
    if (tag) out = out.filter((r) => r.tags?.includes(tag));
    if (query) {
      const q = query.toLowerCase();
      out = out.filter((r) =>
        `${r.text || ""} ${r.title || ""} ${r.customer?.name || ""} ${r.customer?.company || ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    if (sort === "newest") {
      out = [...out].sort(
        (a, b) =>
          new Date(b.dates?.review || 0).getTime() -
          new Date(a.dates?.review || 0).getTime()
      );
    } else if (sort === "longest") {
      out = [...out].sort((a, b) => (b.word_count || 0) - (a.word_count || 0));
    } else if (sort === "top") {
      out = [...out].sort(
        (a, b) =>
          (b.rating || 0) - (a.rating || 0) ||
          (b.word_count || 0) - (a.word_count || 0)
      );
    } else {
      // 'featured' = 5-star with media first, then by length
      out = [...out].sort((a, b) => {
        const aScore =
          (a.rating || 0) * 10 +
          (a.customer?.avatar_url ? 5 : 0) +
          (a.customer?.company_logo_url ? 5 : 0) +
          Math.min(20, Math.floor((a.word_count || 0) / 10));
        const bScore =
          (b.rating || 0) * 10 +
          (b.customer?.avatar_url ? 5 : 0) +
          (b.customer?.company_logo_url ? 5 : 0) +
          Math.min(20, Math.floor((b.word_count || 0) / 10));
        return bScore - aScore;
      });
    }
    return out;
  }, [all, query, tag, category, sort, rating]);

  const tagOrder = ["atai-2026", "newsletter", "tri-women-in-stem", "ai-for-gtm", "aios-desktop", "ai-forward"];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1">
        {/* HERO */}
        <section className="max-w-6xl mx-auto px-6 pt-16 pb-10">
          <div className="grid lg:grid-cols-[1.5fr_1fr] gap-10 items-end">
            <div>
              <div className="inline-flex items-center gap-2 mb-5 text-xs uppercase tracking-widest text-flame font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-flame animate-pulse" />
                Wall of love
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-balance">
                What <span className="text-flame whitespace-nowrap">250,000+</span> community members say about The AIE Network
              </h1>
              <p className="mt-6 text-lg text-white/70 max-w-2xl">
                Reviews from subscribers, event attendees, and training events.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/submit" className="btn-flame">
                  Leave a review
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14m-7-7l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
                <a
                  href="https://theaie.net"
                  className="px-5 py-3 rounded-lg border border-white/15 hover:border-white/35 transition"
                >
                  Visit theAIE.net
                </a>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="stat-block">
                <div className="stat-num">{stats.count}</div>
                <div className="stat-label">Reviews</div>
              </div>
              <div className="stat-block">
                <div className="stat-num">
                  {stats.avg}
                  <span className="text-flame">★</span>
                </div>
                <div className="stat-label">Average</div>
              </div>
              <div className="stat-block">
                <div className="stat-num">250K+</div>
                <div className="stat-label">Readers</div>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED */}
        {featured.length ? (
          <section className="max-w-6xl mx-auto px-6 pb-10">
            <div className="flex items-baseline justify-between mb-5">
              <h2 className="text-xl font-semibold">From the people you know</h2>
              <span className="text-xs uppercase tracking-wide text-white/40">Tap any card to read in full</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
              {featured.map((r) => (
                <div key={r.id} className="min-h-[320px]">
                  <ReviewCard r={r} />
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {/* FILTERS */}
        <section className="max-w-6xl mx-auto px-6 pb-6">
          <div className="card p-4 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search reviews, names, or companies…"
                className="flex-1 min-w-[220px] bg-transparent border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:border-flame"
              />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-ink border border-white/15 rounded-lg px-3 py-2"
              >
                <option value="featured">Most relevant</option>
                <option value="newest">Newest first</option>
                <option value="top">Top rated</option>
                <option value="longest">Most detailed</option>
              </select>
              <select
                value={rating}
                onChange={(e) => setRating(Number(e.target.value))}
                className="bg-ink border border-white/15 rounded-lg px-3 py-2"
              >
                <option value={0}>Any rating</option>
                <option value={5}>5 stars only</option>
                <option value={4}>4★ and up</option>
              </select>
              <span className="ml-auto text-sm text-white/50">
                {filtered.length} of {all.length}
              </span>
            </div>

            <div className="space-y-3">
              {/* Super-categories */}
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-[11px] uppercase tracking-wide text-white/40 mr-1">
                  Browse by
                </span>
                <button
                  onClick={() => { setCategory(""); setTag(""); }}
                  className={`chip ${category === "" && tag === "" ? "is-active" : ""}`}
                >
                  All ({all.length})
                </button>
                {CATEGORIES.filter((c) => categoryCounts[c.key] > 0).map((c) => (
                  <button
                    key={c.key}
                    onClick={() => { setCategory(category === c.key ? "" : c.key); setTag(""); }}
                    className={`chip ${category === c.key ? "is-active" : ""}`}
                  >
                    <span className="mr-1">{c.icon}</span>
                    {c.label}
                    <span className="text-white/40 ml-0.5">({categoryCounts[c.key]})</span>
                  </button>
                ))}
              </div>

              {/* Specific tags within the active category (or all if no category) */}
              <div className="flex flex-wrap gap-1.5">
                {(category
                  ? (CATEGORIES.find((c) => c.key === category)?.tags || [])
                  : tagOrder
                ).filter((t) => tagCounts[t]).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTag(tag === t ? "" : t)}
                    className={`chip text-xs ${tag === t ? "is-active" : ""}`}
                    style={{ padding: "0.25rem 0.625rem" }}
                  >
                    {TAG_LABELS[t] || t}
                    <span className="text-white/40 ml-0.5">({tagCounts[t]})</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* GRID */}
        <section className="max-w-6xl mx-auto px-6 pb-24">
          {loading ? (
            <p className="text-white/50">Loading reviews…</p>
          ) : filtered.length === 0 ? (
            <p className="text-white/50">No reviews match those filters.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
              {filtered.map((r) => (
                <div key={r.id} className="min-h-[320px]">
                  <ReviewCard r={r} />
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
